import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  BiClipboard,
  BiBriefcase,
  BiPieChart,
  BiGroup,
  BiBuilding,
  BiGrid
} from 'react-icons/bi';
import { platformAPI } from '../../services/platformAPI';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { COMPANY_SHORT_NAME, TENDER_APP_NAME } from '../../config/branding';
import './ApplicationsPage.scss';

const APP_ICONS = {
  tender: BiClipboard,
  project_delivery: BiBriefcase,
  finance_visibility: BiPieChart,
  people_policies: BiGroup,
  crm_vendor: BiBuilding
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Available' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'enabled', label: 'Enabled for org' }
];

function displayName(app) {
  return app.key === 'tender' ? TENDER_APP_NAME : app.displayName;
}

const ApplicationsPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useDocumentTitle('Applications');

  useEffect(() => {
    platformAPI.listApplications()
      .then((r) => setApps(r.data?.data?.applications || []))
      .catch((e) => setError(e.response?.data?.message || 'Unable to load applications'))
      .finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(
    () => [...apps].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [apps]
  );

  const stats = useMemo(() => ({
    total: sorted.length,
    active: sorted.filter((a) => a.status === 'active').length,
    roadmap: sorted.filter((a) => a.status === 'planned').length,
    enabled: sorted.filter((a) => a.orgEnabled).length
  }), [sorted]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter((app) => {
      if (filter === 'active' && app.status !== 'active') return false;
      if (filter === 'roadmap' && app.status !== 'planned') return false;
      if (filter === 'enabled' && !app.orgEnabled) return false;
      if (!q) return true;
      const hay = `${displayName(app)} ${app.description} ${app.key}`.toLowerCase();
      return hay.includes(q);
    });
  }, [sorted, filter, search]);

  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="applications-page">
      <WorkspacePageHeader
        title="Applications"
        subtitle={`${COMPANY_SHORT_NAME} Workspace modules licensed and planned for your organisation. Open active apps or review what is on the roadmap.`}
        actions={
          <Button variant="outline-primary" size="sm" onClick={() => navigate('/workspace')}>
            Back to workspace
          </Button>
        }
      />

      <div className="ws-stat-grid">
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">Total modules</div>
          <div className="ws-stat-card__value">{stats.total}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">Available now</div>
          <div className="ws-stat-card__value">{stats.active}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">On roadmap</div>
          <div className="ws-stat-card__value">{stats.roadmap}</div>
        </div>
        <div className="ws-stat-card">
          <div className="ws-stat-card__label">Enabled for org</div>
          <div className="ws-stat-card__value">{stats.enabled}</div>
        </div>
      </div>

      <div className="applications-page__toolbar">
        <div className="applications-page__filters" role="tablist" aria-label="Filter applications">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`applications-page__filter-btn ${filter === f.id ? 'is-active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="applications-page__search">
          <Form.Control
            type="search"
            placeholder="Search applications…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search applications"
          />
        </div>
      </div>

      <div className="ws-section-label">Catalog ({visible.length})</div>

      {visible.length === 0 ? (
        <div className="ws-panel text-muted small">No applications match your filters.</div>
      ) : (
        <div className="ws-app-grid">
          {visible.map((app) => {
            const Icon = APP_ICONS[app.key] || BiGrid;
            const isRoadmap = app.status === 'planned';
            const canOpen = app.key === 'tender' && app.orgEnabled && app.userCanAccess;
            const noAccess = app.key === 'tender' && app.orgEnabled && !app.userCanAccess;

            return (
              <article
                key={app.key}
                className={`ws-app-card ${isRoadmap ? 'ws-app-card--roadmap ws-app-card--muted' : ''}`}
              >
                <div className="ws-app-card__top">
                  <div className="ws-app-card__icon" aria-hidden>
                    <Icon />
                  </div>
                  <div className="flex-grow-1">
                    <div className="ws-app-card__head">
                      <h2 className="ws-app-card__title">{displayName(app)}</h2>
                      <Badge bg={isRoadmap ? 'secondary' : 'success'} className="fw-normal">
                        {isRoadmap ? 'Roadmap' : 'Active'}
                      </Badge>
                    </div>
                    <div className="ws-app-card__meta">
                      {app.orgEnabled ? (
                        <Badge bg="light" text="dark" className="border fw-normal">Org enabled</Badge>
                      ) : (
                        <Badge bg="light" text="muted" className="border fw-normal">Not enabled</Badge>
                      )}
                      {app.userCanAccess ? (
                        <Badge bg="light" text="dark" className="border fw-normal">You have access</Badge>
                      ) : null}
                    </div>
                  </div>
                </div>
                <p className="ws-app-card__desc">{app.description}</p>
                {isRoadmap ? (
                  <p className="ws-app-card__note">Planned for a future release — not operational in this workspace yet.</p>
                ) : null}
                {noAccess ? (
                  <div className="ws-app-card__alert" role="status">
                    You do not have access. Contact a platform administrator to assign roles.
                  </div>
                ) : null}
                <div className="ws-app-card__foot">
                  {canOpen ? (
                    <Button variant="primary" size="sm" onClick={() => navigate('/apps/tender')}>
                      Open {TENDER_APP_NAME}
                    </Button>
                  ) : (
                    <Button variant="outline-secondary" size="sm" disabled>
                      {isRoadmap ? 'Coming soon' : 'Unavailable'}
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <section className="ws-panel mt-4">
        <h2 className="ws-panel__title">Managing entitlements</h2>
        <p className="small text-muted mb-0">
          Organisation-level application toggles are configured during{' '}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => navigate('/platform/company')}>
            company onboarding
          </button>
          {' '}or by a platform administrator. User access depends on assigned roles.
        </p>
      </section>
    </div>
  );
};

export default ApplicationsPage;
